import React, { useState, useEffect, useCallback } from 'react';
import { HiCheck, HiTrash } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';

const todayISO = () => new Date().toISOString().slice(0, 10);

/** A UTC instant -> value for a <input type="datetime-local"> in Qatar time. */
const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    // Shift to Qatar (UTC+3) then format as yyyy-MM-ddTHH:mm.
    const qatar = new Date(d.getTime() + 3 * 3600 * 1000);
    return qatar.toISOString().slice(0, 16);
};

/** datetime-local value (read as Qatar wall-clock) -> UTC ISO string. */
const fromLocalInput = (value) => {
    if (!value) return null;
    return new Date(`${value}:00+03:00`).toISOString();
};

/**
 * Admin correction of check-in / check-out punches for one Qatar day.
 *
 * An un-checked-out shift otherwise runs forever, so a missed or wrong punch
 * has to be fixable here. Every edit stamps the row `source: admin`.
 */
export default function AttendanceTab() {
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [busyId, setBusyId] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        return axiosInstance.get('/api/working-time/attendance/daily', { params: { date } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Could not load attendance'))
            .finally(() => setLoading(false));
    }, [date]);

    useEffect(() => { load(); }, [load]);

    const savePunch = (punch, patch) => {
        setBusyId(punch._id);
        setError('');
        axiosInstance.patch(`/api/working-time/attendance/${punch._id}`, patch)
            .then(load)
            .catch((e) => setError(e.response?.data?.message || 'Could not save the punch'))
            .finally(() => setBusyId(''));
    };

    const removePunch = (punch) => {
        if (!window.confirm('Remove this punch?')) return;
        setBusyId(punch._id);
        setError('');
        axiosInstance.delete(`/api/working-time/attendance/${punch._id}`)
            .then(load)
            .catch((e) => setError(e.response?.data?.message || 'Could not remove the punch'))
            .finally(() => setBusyId(''));
    };

    return (
        <div>
            <div className="wt-toolbar">
                <div className="wt-field">
                    <label htmlFor="wt-att-date">Date</label>
                    <input
                        id="wt-att-date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                    />
                </div>
                <button type="button" className="wt-btn" onClick={load} disabled={loading}>
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {error && <div className="wt-error">{error}</div>}

            {data && data.employees.length === 0 && (
                <div className="wt-empty">No check-ins recorded on {data.date}.</div>
            )}

            {data && data.employees.map((emp) => (
                <div className="wt-hero wt-punch" key={emp.userId}>
                    <div className="wt-hero-head">
                        <div>
                            <h2 className="wt-hero-title">{emp.name}</h2>
                            <div className="wt-hero-date">
                                {emp.employeeCode} · {emp.department || 'No department'}
                            </div>
                        </div>
                    </div>

                    <div className="wt-punch-scroll">
                    <table className="wt-punch-table">
                        <thead>
                            <tr>
                                <th className="wt-punch-actions-col" />
                                <th>Check in</th>
                                <th>Check out</th>
                                <th>Elapsed</th>
                                <th>Active</th>
                                <th>Idle</th>
                                <th title="Screen locked, signed out, laptop asleep or off, or agent not running. The agent cannot tell these apart.">
                                    Away (locked / off)
                                </th>
                                <th>Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emp.punches.map((p) => (
                                <PunchRow
                                    key={p._id}
                                    punch={p}
                                    busy={busyId === p._id}
                                    onSave={savePunch}
                                    onRemove={removePunch}
                                />
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PunchRow({ punch, busy, onSave, onRemove }) {
    const [checkIn, setCheckIn] = useState(toLocalInput(punch.checkInAt));
    const [checkOut, setCheckOut] = useState(toLocalInput(punch.checkOutAt));

    useEffect(() => {
        setCheckIn(toLocalInput(punch.checkInAt));
        setCheckOut(toLocalInput(punch.checkOutAt));
    }, [punch.checkInAt, punch.checkOutAt]);

    const dirty = checkIn !== toLocalInput(punch.checkInAt)
        || checkOut !== toLocalInput(punch.checkOutAt);

    return (
        <tr>
            <td className="wt-punch-actions-col">
                <div className="wt-punch-actions">
                    <button
                        type="button"
                        className="wt-icon-btn primary"
                        title="Save"
                        aria-label="Save"
                        disabled={busy || !dirty}
                        onClick={() => onSave(punch, {
                            checkInAt: fromLocalInput(checkIn),
                            checkOutAt: checkOut ? fromLocalInput(checkOut) : null,
                        })}
                    >
                        <HiCheck />
                    </button>
                    <button
                        type="button"
                        className="wt-icon-btn danger"
                        title="Delete"
                        aria-label="Delete"
                        disabled={busy}
                        onClick={() => onRemove(punch)}
                    >
                        <HiTrash />
                    </button>
                </div>
            </td>
            <td data-label="Check in">
                <input
                    type="datetime-local"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                />
            </td>
            <td data-label="Check out">
                <input
                    type="datetime-local"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                />
                {punch.open && <span className="wt-badge good">open</span>}
            </td>
            <td data-label="Elapsed">{punch.elapsedHours || '—'}</td>
            <td data-label="Active">
                {!punch.windowed ? '—' : punch.windowed.activeHours}
            </td>
            <td data-label="Idle">
                {!punch.windowed ? '—' : punch.windowed.idleHours}
            </td>
            <td data-label="Away (locked / off)">
                {!punch.windowed ? '—' : punch.windowed.awayHours}
                {punch.windowed && !punch.windowed.sampled && (
                    <span
                        className="wt-badge"
                        title="Part of this day was uploaded late (the laptop was offline), so the split between active, idle and away is approximate."
                    >
                        approx.
                    </span>
                )}
            </td>
            <td data-label="Source">{punch.source}</td>
        </tr>
    );
}
